import React, { Fragment, useContext } from "react";
import TextoExpandible from "../common/TextoExpandible";
import { quitarClaveNombre } from "../../utils/destajos";
import { FILES_ENDPOINT, isSameDate } from "../../utils";
import { AuthContext } from "../../context/AuthContext";
import AdjuntoEntrada from "./AdjuntoEntrada";
import StatusBadge from "../common/StatusBadge";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const Mensaje = ({ retraso, entrada, fechaAnterior }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  function isFollowUp() {
    return (
      isSameDate(fechaAnterior, entrada.createdAt) && !retraso && fechaAnterior
    );
  }

  function renderUserImg() {
    if (entrada.Usuario && entrada.Usuario !== null) {
      if (entrada.Usuario.Adjunto !== null) {
        const { Adjunto } = entrada.Usuario;
        return (
          <img
            src={`${FILES_ENDPOINT}/${Adjunto.idAdjunto}.${Adjunto.tipo}`}
            className="bitacoraImage mt-3"
            style={{ boxShadow: "0 0 8px rgba(0,0,0,.2)", borderRadius: "50%" }}
            alt={Adjunto.nombre}
          />
        );
      }
    }
    return <i className="fa fa-user-circle fa-2x d-md-block d-inline"></i>;
  }

  function renderUser() {
    if (entrada.idUsuario !== user.idUsuario) {
      return (
        <div className="col-12 col-md-1 my-2 my-md-0">
          <div className="row align-items-center">
            <div className="col-md-12 col-1">{renderUserImg()}</div>
            <div className="col-md-12 col-11 d-md-none">
              <span className="d-md-none d-inline-block ms-2">
                {renderName()}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return <div className="col"></div>;
  }

  function renderContenido() {
    if (entrada && entrada !== null) {
      if (entrada.contenido !== null && entrada.contenido !== "") {
        return (
          <div
            className="card-text"
            style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {entrada.contenido}
          </div>
        );
      }
    }
  }

  function renderAdjuntos() {
    if (entrada && entrada !== null) {
      if (Array.isArray(entrada.adjuntos)) {
        return entrada.adjuntos
          .filter((adjunto) => adjunto && adjunto !== null)
          .map((adjunto) => (
            <AdjuntoEntrada key={adjunto.idAdjunto} adjunto={adjunto} />
          ));
      }
    }
  }

  function renderPermiso() {
    if (entrada.Usuario.PermisoProyectos) {
      return (
        <span className="badge ms-2 small text-capitalize badge-pill bg-dark">
          {entrada.Usuario.PermisoProyectos[0]?.Permiso?.nombre}
        </span>
      );
    }
  }

  function renderName() {
    if (
      entrada.Usuario &&
      entrada.Usuario !== null &&
      entrada.idUsuario !== user.idUsuario
    ) {
      return (
        <span className="small mb-2">
          {entrada.Usuario.nombre}
          {renderPermiso()}
        </span>
      );
    }
  }

  function renderFecha() {
    if (!isFollowUp()) {
      return (
        <div className="row mb-2 mb-md-3">
          <div className="container text-center my-2">
            <span className="bg-light small border p-2 shadow-sm round5">
              {moment(entrada.createdAt).format("DD MMM YYYY")}
            </span>
          </div>
        </div>
      );
    }
  }

  // Where the comment comes from: partida › destajo [clave] › subdestajo.
  const renderRubro = () => {
    const origen = entrada.origen || {};
    const partida = origen.partida || entrada.Partida?.nombre;
    // A concepto comment already shows "[clave] concepto" inside the message
    // card (renderContexto), so only the partida is added on top.
    const destajo = entrada.Concepto
      ? null
      : origen.destajo || entrada.Destajo?.nombre;
    const subdestajo = origen.subdestajo || entrada.SubDestajo?.nombre;
    if (!partida && !destajo && !subdestajo) return null;
    const propio = entrada.idUsuario === user.idUsuario;
    return (
      <div
        className={`small mb-1 d-inline-flex flex-column ${
          propio ? "align-items-end text-end" : ""
        } ${entrada.retraso ? "text-danger" : "text-muted"}`}
        style={{ maxWidth: 600 }}
      >
        {partida && (
          <span className="text-truncate" style={{ maxWidth: "100%" }} title={partida}>
            <i className="fa fa-folder me-1" />
            {partida}
          </span>
        )}
        {destajo && (
          <TextoExpandible>
            <i className="fa fa-angle-right me-1" />
            {origen.clave && <span className="me-1">[{origen.clave}]</span>}
            <span className="text-dark">
              {quitarClaveNombre(destajo, origen.clave)}
            </span>
          </TextoExpandible>
        )}
        {subdestajo && (
          <span className="text-truncate" style={{ maxWidth: "100%" }} title={subdestajo}>
            <i className="fa fa-angle-right me-1" />
            {subdestajo}
          </span>
        )}
      </div>
    );
  };

  const renderContexto = () => {
    const concepto = entrada.Concepto;
    const ev = entrada.EstimacionVersion;
    if (!concepto && !ev) return null;

    const idContrato = ev?.Estimacion?.idContrato;
    const canNavigate = concepto && ev && idContrato;

    const handleContextoClick = () => {
      if (!canNavigate) return;
      navigate(
        `/obra/${entrada.idProyecto}/finanzas/${idContrato}/estimacion/${ev.idEstimacion}/versiones/${ev.idEstimacionVersion}?idConcepto=${concepto.idConcepto}`
      );
    };

    return (
      <div
        className={`rounded p-2 mb-2${canNavigate ? " cursor-pointer" : ""}`}
        onClick={handleContextoClick}
        style={{
          background: "rgba(0,0,0,0.06)",
          borderLeft: "3px solid rgba(0,0,0,0.15)",
          cursor: canNavigate ? "pointer" : "default",
        }}
      >
        {concepto && (
          <div
            className="fw-semibold small text-truncate mb-1"
            title={`${concepto.clave} ${concepto.concepto}`}
          >
            [{concepto.clave}] {concepto.concepto}
          </div>
        )}
        {ev && (
          <div className="d-flex align-items-center gap-2 mt-1">
            {ev.Estimacion && (
              <span className="fw-semibold small">Estimación #{ev.Estimacion.numero} v{ev.version}</span>
            )}
            <span style={{ transform: "scale(0.8)", transformOrigin: "left center", display: "inline-block" }}>
              <StatusBadge text={ev.estado} small />
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderCol = () => {
    if (entrada.idUsuario !== user.idUsuario) {
      return <div className="col" />;
    }
  };

  return (
    <Fragment>
      {renderFecha()}
      <div className="row mb-2 mb-md-3" style={{ minHeight: 70 }}>
        {renderUser()}
        <div className="col-11 col-md-10" style={{ minWidth: 250 }}>
          <div className="d-none d-md-block mb-2">{renderName()}</div>
          <div
            className={entrada.idUsuario === user.idUsuario ? "text-end" : ""}
          >
            {renderRubro()}
          </div>
          <div
            className={`card shadow-sm msg-card py-3 ${
              entrada.idUsuario === user.idUsuario
                ? "bg-primary text-white me-0 ms-auto"
                : "bg-white ms-0 me-auto"
            }`}
            style={{
              maxWidth: entrada.adjuntos.length <= 1 ? "600px" : "100%",
              overflow: "hidden",
            }}
          >
            <div className="container-fluid" style={{ maxWidth: "100%" }}>
              {renderContexto()}
              <div style={{ maxWidth: "100%", overflow: "hidden" }}>
                {renderAdjuntos()}
              </div>
              {renderContenido()}
            </div>
            <div className="container-fluid mt-2">
              <p className="mb-0 small" style={{ minWidth: 42 }}>
                {entrada ? moment(entrada.createdAt).format("HH:mm") : ""}
              </p>
            </div>
          </div>
        </div>
        {renderCol()}
      </div>
    </Fragment>
  );
};

export default Mensaje;
